import maplibregl from 'maplibre-gl';
import store from '../state';

export interface LightningHistoryScan {
  ts: string;
  epoch: number;
  // [lat, lon, max_dens, sum_dens, hits]
  cells: number[][];
}

export interface LightningHistoryPayload {
  version: number;
  cell_bin_deg: number;
  step_minutes: number;
  trail_hours: number;
  history_hours: number;
  latest_scan: string | null;
  latest_epoch: number;
  scans: LightningHistoryScan[];
}

interface DecayColorStop {
  frac: number;
  rgb: [number, number, number];
}

interface LightningVectorConfig {
  baseIconScale: number;
  densityScaleMin: number;
  densityScaleMax: number;
  densityLogOffset: number;
  densityLogSpan: number;
  cellBinDeg: number;
  trailHours: number;
  stepMinutes: number;
  ageBuckets: number;
  minAgeAlpha: number;
  allowOverlap: boolean;
  zoomStops: [number, number][];
  decayColorStops: DecayColorStop[];
}

interface TrailFeatureItem {
  lon: number;
  lat: number;
  mercX: number;
  mercY: number;
  bucket: number;
  ageMin: number;
  ageAlpha: number;
  density: number;
  hits: number;
  dScale: number;
  sortKey: number;
}

const DEG2RAD = Math.PI / 180;
const QUARTER_PI = Math.PI * 0.25;
const INV_TWO_PI = 0.5 / Math.PI;

function lonToMercX(lon: number): number {
  return (lon + 180) / 360;
}

function latToMercY(lat: number): number {
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat));
  return 0.5 - Math.log(Math.tan(QUARTER_PI + clamped * DEG2RAD * 0.5)) * INV_TWO_PI;
}

interface LightningPointFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
  properties: {
    bucket: number;
    ageMin: number;
    ageAlpha: number;
    density: number;
    hits: number;
    dScale: number;
    sortKey: number;
  };
}

interface LightningFeatureCollection {
  type: 'FeatureCollection';
  features: LightningPointFeature[];
}

const DEFAULT_DECAY_STOPS: DecayColorStop[] = [
  { frac: 0.00, rgb: [255, 232, 0] },
  { frac: 0.05, rgb: [255, 198, 0] },
  { frac: 0.14, rgb: [245, 158, 11] },
  { frac: 0.32, rgb: [204, 138, 68] },
  { frac: 0.62, rgb: [134, 140, 150] },
  { frac: 1.00, rgb: [96, 108, 124] }
];

const DEFAULT_ZOOM_STOPS: [number, number][] = [
  [2.5, 0.36],
  [4.0, 0.52],
  [6.0, 0.78],
  [8.5, 1.12],
  [11.0, 1.48]
];

function loadLightningVectorConfig(): LightningVectorConfig {
  try {
    const raw =
      (typeof __APP_CONFIG__ !== 'undefined'
        ? ((__APP_CONFIG__ as any)?.live_weather?.lightning_vector ||
           (__APP_CONFIG__ as any)?.ui?.live_weather?.lightning_vector)
        : undefined) || {};

    const parsedZoomStops: [number, number][] = [];
    if (Array.isArray(raw.zoom_stops)) {
      for (const pair of raw.zoom_stops) {
        if (Array.isArray(pair) && pair.length >= 2) {
          const z = Number(pair[0]);
          const sc = Number(pair[1]);
          if (Number.isFinite(z) && Number.isFinite(sc)) {
            parsedZoomStops.push([z, sc]);
          }
        }
      }
    }
    const zoomStops: [number, number][] =
      parsedZoomStops.length >= 2 ? parsedZoomStops : DEFAULT_ZOOM_STOPS;

    const parsedDecayStops: DecayColorStop[] = [];
    if (Array.isArray(raw.decay_color_stops)) {
      for (const s of raw.decay_color_stops) {
        if (Array.isArray(s) && s.length >= 4) {
          const frac = Number(s[0]);
          const r = Number(s[1]);
          const g = Number(s[2]);
          const b = Number(s[3]);
          if ([frac, r, g, b].every(Number.isFinite)) {
            parsedDecayStops.push({ frac, rgb: [r, g, b] });
          }
        } else if (s && typeof s === 'object' && Array.isArray(s.rgb) && s.rgb.length >= 3) {
          const frac = Number(s.frac);
          const r = Number(s.rgb[0]);
          const g = Number(s.rgb[1]);
          const b = Number(s.rgb[2]);
          if ([frac, r, g, b].every(Number.isFinite)) {
            parsedDecayStops.push({ frac, rgb: [r, g, b] });
          }
        }
      }
    }
    const decayColorStops: DecayColorStop[] =
      parsedDecayStops.length >= 2 ? parsedDecayStops : DEFAULT_DECAY_STOPS;

    return {
      baseIconScale: Number(raw.base_icon_scale) || 0.9,
      densityScaleMin: Number(raw.density_scale_min) || 0.6,
      densityScaleMax: Number(raw.density_scale_max) || 2.0,
      densityLogOffset: Number(raw.density_log_offset) || 0.45,
      densityLogSpan: Number(raw.density_log_span) || 1.35,
      cellBinDeg: Number(raw.cell_bin_deg) || 0.04,
      trailHours: Number(raw.trail_hours) || 8,
      stepMinutes: Number(raw.step_minutes) || 5,
      ageBuckets: Number(raw.age_buckets) || 25,
      minAgeAlpha: Number(raw.min_age_alpha) || 0.58,
      allowOverlap: raw.allow_overlap !== false,
      zoomStops,
      decayColorStops
    };
  } catch (err) {
    console.warn('[VectorLightningRenderer] Failed to parse lightning_vector config, using defaults:', err);
    return {
      baseIconScale: 0.9,
      densityScaleMin: 0.6,
      densityScaleMax: 2.0,
      densityLogOffset: 0.45,
      densityLogSpan: 1.35,
      cellBinDeg: 0.04,
      trailHours: 8,
      stepMinutes: 5,
      ageBuckets: 25,
      minAgeAlpha: 0.58,
      allowOverlap: true,
      zoomStops: DEFAULT_ZOOM_STOPS,
      decayColorStops: DEFAULT_DECAY_STOPS
    };
  }
}

export class VectorLightningRenderer {
  private map: maplibregl.Map;
  private cfg: LightningVectorConfig;

  private readonly sourceId = 'live-weather-lightning-vec-source';
  private readonly layerId = 'live-weather-lightning-vec-symbols';
  private readonly spritePrefix = 'gw-bolt-';

  // High-DPI overlay canvas at z-index 3 so bolts remain razor-sharp above DOM radar (z=2) & satellite (z=1) canvases
  private overlayCanvas: HTMLCanvasElement;
  private overlayCtx: CanvasRenderingContext2D;
  private spriteCanvases: HTMLCanvasElement[] = [];

  private historyCache: Map<string, LightningHistoryPayload> = new Map();
  private pendingFetches: Map<string, Promise<LightningHistoryPayload | null>> = new Map();
  private latestHistory: LightningHistoryPayload | null = null;
  private currentHistoryUrl: string | null = null;

  private cachedGeoJsonKey: string = '';
  private cachedGeoJson: LightningFeatureCollection = {
    type: 'FeatureCollection',
    features: []
  };
  private cachedItems: TrailFeatureItem[] = [];

  private lastSyncedGeoJsonKey: string = '';
  private lastSyncedVisible: boolean | null = null;
  private lastSyncedOpacity: number = -1;
  private overlayHasContent: boolean = false;
  private lastDrawnCanvasKey: string = '';
  private lastHoverCheckMs: number = 0;
  private canvasHoverActive: boolean = false;

  private targetEpochSec: number = 0;
  private currentOpacity: number = 1.0;
  private active: boolean = false;
  private useCanvasComposite: boolean = false;
  private interactionsBound: boolean = false;
  private hoverPopup: maplibregl.Popup | null = null;

  private onMapMoveBound: () => void;
  private onMapResizeBound: () => void;
  private onStyleDataBound: () => void;
  private onCanvasMouseMoveBound: (ev: any) => void;

  constructor(map: maplibregl.Map) {
    this.map = map;
    this.cfg = loadLightningVectorConfig();

    this.overlayCanvas = document.createElement('canvas');
    this.overlayCanvas.id = 'live-lightning-vec-canvas';
    this.overlayCanvas.style.position = 'absolute';
    this.overlayCanvas.style.top = '0';
    this.overlayCanvas.style.left = '0';
    this.overlayCanvas.style.width = '100%';
    this.overlayCanvas.style.height = '100%';
    this.overlayCanvas.style.pointerEvents = 'none';
    this.overlayCanvas.style.zIndex = '3';
    map.getContainer().appendChild(this.overlayCanvas);

    this.overlayCtx = this.overlayCanvas.getContext('2d')!;
    this.buildSpriteCanvases();
    this.resizeOverlayCanvas();

    this.onMapMoveBound = () => {
      this.lastDrawnCanvasKey = '';
      if (this.active && this.useCanvasComposite) {
        this.redrawOverlayCanvas();
      }
    };
    this.onMapResizeBound = () => {
      this.lastDrawnCanvasKey = '';
      this.resizeOverlayCanvas();
      if (this.active && this.useCanvasComposite) {
        this.redrawOverlayCanvas();
      }
    };
    // Only re-attach MapLibre source/layer if a basemap style switch wiped them (never loop on setData styledata events)
    this.onStyleDataBound = () => {
      if (
        this.active &&
        !this.useCanvasComposite &&
        this.latestHistory &&
        !this.map.getLayer(this.layerId)
      ) {
        this.lastSyncedGeoJsonKey = '';
        this.lastSyncedVisible = null;
        this.lastSyncedOpacity = -1;
        this.syncMapLibreLayer();
      }
    };
    this.onCanvasMouseMoveBound = (ev: any) => {
      this.handleCanvasHover(ev);
    };

    this.map.on('move', this.onMapMoveBound);
    this.map.on('resize', this.onMapResizeBound);
    this.map.on('styledata', this.onStyleDataBound);
    this.map.on('mousemove', this.onCanvasMouseMoveBound);
  }

  private resizeOverlayCanvas(): void {
    const container = this.map.getContainer();
    const dpr = window.devicePixelRatio || 1;
    const w = Math.max(1, container.clientWidth);
    const h = Math.max(1, container.clientHeight);
    this.overlayCanvas.width = Math.floor(w * dpr);
    this.overlayCanvas.height = Math.floor(h * dpr);
    this.overlayCanvas.style.width = `${w}px`;
    this.overlayCanvas.style.height = `${h}px`;
    this.overlayCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private interpolateDecayRgb(frac: number): [number, number, number] {
    const stops = this.cfg.decayColorStops;
    const t = Math.max(0, Math.min(1, frac));
    for (let i = 0; i < stops.length - 1; i++) {
      const a = stops[i];
      const b = stops[i + 1];
      if (t >= a.frac && t <= b.frac) {
        const local = (t - a.frac) / Math.max(1e-6, b.frac - a.frac);
        return [
          Math.round(a.rgb[0] + (b.rgb[0] - a.rgb[0]) * local),
          Math.round(a.rgb[1] + (b.rgb[1] - a.rgb[1]) * local),
          Math.round(a.rgb[2] + (b.rgb[2] - a.rgb[2]) * local)
        ];
      }
    }
    return stops[stops.length - 1].rgb;
  }

  private drawBoltOnCanvas(canvas: HTMLCanvasElement, bucketIdx: number, logicalSize: number): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const scale = logicalSize / 64;
    const frac = bucketIdx / Math.max(1, this.cfg.ageBuckets - 1);
    const [r, g, b] = this.interpolateDecayRgb(frac);
    const fillCss = `rgb(${r}, ${g}, ${b})`;
    const isFresh = bucketIdx <= 1;

    ctx.clearRect(0, 0, logicalSize, logicalSize);
    ctx.save();
    ctx.scale(scale, scale);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';

    if (isFresh) {
      ctx.shadowColor = 'rgba(255, 230, 0, 0.65)';
      ctx.shadowBlur = 8 * scale;
    }

    const bolt = new Path2D('M36.5 6 L14.5 34.5 H29.5 L25.5 58 L49.5 28.5 H33.5 Z');
    ctx.lineWidth = 5.5;
    ctx.strokeStyle = `rgba(10, 13, 20, ${(0.94 - 0.32 * frac).toFixed(3)})`;
    ctx.stroke(bolt);
    ctx.shadowBlur = 0;

    ctx.fillStyle = fillCss;
    ctx.fill(bolt);

    if (bucketIdx === 0) {
      const inner = new Path2D('M34.5 14 L21 32.5 H30.5 L28.5 47 L42.5 29.5 H32.5 Z');
      ctx.fillStyle = 'rgba(255, 252, 210, 0.55)';
      ctx.fill(inner);
    }

    ctx.restore();
  }

  private buildSpriteCanvases(): void {
    const dpr = typeof window !== 'undefined' ? Math.max(2, Math.ceil(window.devicePixelRatio || 2)) : 2;
    const pxSize = 32 * dpr;
    this.spriteCanvases = [];
    for (let b = 0; b < this.cfg.ageBuckets; b++) {
      const c = document.createElement('canvas');
      c.width = pxSize;
      c.height = pxSize;
      this.drawBoltOnCanvas(c, b, pxSize);
      this.spriteCanvases.push(c);
    }
  }

  private registerMapLibreSprites(): void {
    const size = 64;
    const scratch = document.createElement('canvas');
    scratch.width = size;
    scratch.height = size;
    const ctx = scratch.getContext('2d');
    if (!ctx) return;

    for (let b = 0; b < this.cfg.ageBuckets; b++) {
      const id = `${this.spritePrefix}${b}`;
      this.drawBoltOnCanvas(scratch, b, size);
      const imgData = ctx.getImageData(0, 0, size, size);
      if (this.map.hasImage(id)) {
        this.map.updateImage(id, imgData);
      } else {
        this.map.addImage(id, imgData, { pixelRatio: 2 });
      }
    }
  }

  private computeDensityScale(density: number): number {
    const norm = Math.max(
      0,
      Math.min(1, (Math.log1p(Math.max(0, density)) - this.cfg.densityLogOffset) / this.cfg.densityLogSpan)
    );
    const val = this.cfg.densityScaleMin + norm * (this.cfg.densityScaleMax - this.cfg.densityScaleMin);
    return Number(val.toFixed(3));
  }

  private evaluateZoomScale(zoom: number): number {
    const stops = this.cfg.zoomStops;
    if (zoom <= stops[0][0]) return stops[0][1] * this.cfg.baseIconScale;
    const last = stops[stops.length - 1];
    if (zoom >= last[0]) return last[1] * this.cfg.baseIconScale;
    for (let i = 0; i < stops.length - 1; i++) {
      const [z0, s0] = stops[i];
      const [z1, s1] = stops[i + 1];
      if (zoom >= z0 && zoom <= z1) {
        const t = (zoom - z0) / Math.max(1e-6, z1 - z0);
        return (s0 + (s1 - s0) * t) * this.cfg.baseIconScale;
      }
    }
    return last[1] * this.cfg.baseIconScale;
  }

  private buildIconSizeExpression(): any[] {
    const expr: any[] = ['interpolate', ['linear'], ['zoom']];
    for (const [z, factor] of this.cfg.zoomStops) {
      expr.push(z, ['*', ['get', 'dScale'], Number((factor * this.cfg.baseIconScale).toFixed(4))]);
    }
    return expr;
  }

  private ageStepToBucket(ageSteps: number): number {
    if (ageSteps <= 0) return 0;
    const totalSteps = Math.max(1, Math.round((this.cfg.trailHours * 60) / this.cfg.stepMinutes) - 1);
    const frac = Math.min(1, ageSteps / totalSteps);
    return Math.min(
      this.cfg.ageBuckets - 1,
      Math.max(1, Math.round(frac * (this.cfg.ageBuckets - 1)))
    );
  }

  private async fetchHistory(url: string): Promise<LightningHistoryPayload | null> {
    if (this.historyCache.has(url)) {
      return this.historyCache.get(url)!;
    }
    if (this.pendingFetches.has(url)) {
      return this.pendingFetches.get(url)!;
    }

    const promise = fetch(url, { credentials: 'include' })
      .then(async (resp) => {
        if (!resp.ok) return null;
        const json = (await resp.json()) as LightningHistoryPayload;
        if (!json || !Array.isArray(json.scans)) return null;
        // Bound cache size to last 4 manifest versions
        if (this.historyCache.size >= 4) {
          const oldestKey = this.historyCache.keys().next().value;
          if (oldestKey) this.historyCache.delete(oldestKey);
        }
        this.historyCache.set(url, json);
        return json;
      })
      .catch(() => null)
      .finally(() => {
        this.pendingFetches.delete(url);
      });

    this.pendingFetches.set(url, promise);
    return promise;
  }

  private buildRollingTrail(history: LightningHistoryPayload, targetEpochSec: number): void {
    const scans = history.scans;
    if (!scans || scans.length === 0) {
      if (this.cachedGeoJsonKey !== 'empty') {
        this.cachedGeoJsonKey = 'empty';
        this.cachedItems = [];
        this.cachedGeoJson = { type: 'FeatureCollection', features: [] };
      }
      return;
    }

    // Locate latest scan at or before targetEpochSec (+60s tolerance for sub-minute scan jitter)
    let endIdx = -1;
    for (let i = scans.length - 1; i >= 0; i--) {
      if (scans[i].epoch <= targetEpochSec + 60) {
        endIdx = i;
        break;
      }
    }
    if (endIdx < 0) {
      // Target time precedes all available scans
      endIdx = 0;
    }

    const endScanEpoch = scans[endIdx].epoch;
    // Anchor to endScanEpoch when within normal 15-min live cadence; reflect true age if feed is stale
    const effectiveEndEpoch =
      Math.abs(targetEpochSec - endScanEpoch) <= 900 ? endScanEpoch : Math.max(targetEpochSec, endScanEpoch);

    const stepSec = Math.max(60, this.cfg.stepMinutes * 60);
    const trailSec = this.cfg.trailHours * 3600;
    const windowStartEpoch = effectiveEndEpoch - trailSec;

    const cacheKey = `${history.latest_scan || history.latest_epoch}|${endIdx}|${Math.floor(effectiveEndEpoch / stepSec)}`;
    if (this.cachedGeoJsonKey === cacheKey) {
      return;
    }

    const binDeg = this.cfg.cellBinDeg;
    const cellMap = new Map<
      number,
      {
        minAgeSec: number;
        maxDens: number;
        wLat: number;
        wLon: number;
        wSum: number;
        hits: number;
      }
    >();

    // Iterate oldest -> newest across the 8h window so newest strike updates minAgeSec
    for (let i = 0; i <= endIdx; i++) {
      const sc = scans[i];
      if (sc.epoch < windowStartEpoch) continue;
      const ageSec = Math.max(0, effectiveEndEpoch - sc.epoch);
      const cells = sc.cells;
      if (!cells || cells.length === 0) continue;

      for (let j = 0; j < cells.length; j++) {
        const c = cells[j];
        const lat = c[0];
        const lon = c[1];
        const maxD = c[2];
        const sumD = c.length >= 4 ? c[3] : maxD;
        const hits = c.length >= 5 ? c[4] : 1;

        const rBin = Math.floor(lat / binDeg);
        const cBin = Math.floor(lon / binDeg);
        const cellKey = rBin * 1000000 + (cBin + 500000);

        const rec = cellMap.get(cellKey);
        if (!rec) {
          cellMap.set(cellKey, {
            minAgeSec: ageSec,
            maxDens: maxD,
            wLat: lat * sumD,
            wLon: lon * sumD,
            wSum: sumD,
            hits
          });
        } else {
          if (ageSec < rec.minAgeSec) rec.minAgeSec = ageSec;
          if (maxD > rec.maxDens) rec.maxDens = maxD;
          rec.wLat += lat * sumD;
          rec.wLon += lon * sumD;
          rec.wSum += sumD;
          rec.hits += hits;
        }
      }
    }

    const totalSteps = Math.max(1, Math.round((this.cfg.trailHours * 60) / this.cfg.stepMinutes) - 1);
    const alphaDrop = 1.0 - this.cfg.minAgeAlpha;
    const items: TrailFeatureItem[] = [];

    cellMap.forEach((rec) => {
      if (rec.wSum <= 0) return;
      const lat = Number((rec.wLat / rec.wSum).toFixed(4));
      const lon = Number((rec.wLon / rec.wSum).toFixed(4));
      const ageSteps = rec.minAgeSec <= stepSec * 0.6 ? 0 : Math.max(1, Math.round(rec.minAgeSec / stepSec));
      const bucket = this.ageStepToBucket(ageSteps);
      const ageFrac = Math.min(1, ageSteps / totalSteps);
      const ageAlpha = Number((1.0 - alphaDrop * ageFrac).toFixed(3));
      const dScale = this.computeDensityScale(rec.maxDens);
      const priority = (totalSteps + 1 - Math.min(totalSteps + 1, ageSteps)) * 100 + Math.min(99, rec.maxDens * 10);
      const sortKey = this.cfg.allowOverlap ? priority : -priority;

      items.push({
        lon,
        lat,
        mercX: lonToMercX(lon),
        mercY: latToMercY(lat),
        bucket,
        ageMin: ageSteps * this.cfg.stepMinutes,
        ageAlpha,
        density: Number(rec.maxDens.toFixed(4)),
        hits: rec.hits,
        dScale,
        sortKey
      });
    });

    items.sort((a, b) => a.sortKey - b.sortKey);

    const features: LightningPointFeature[] = items.map((it) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [it.lon, it.lat]
      },
      properties: {
        bucket: it.bucket,
        ageMin: it.ageMin,
        ageAlpha: it.ageAlpha,
        density: it.density,
        hits: it.hits,
        dScale: it.dScale,
        sortKey: it.sortKey
      }
    }));

    this.cachedItems = items;
    this.cachedGeoJson = {
      type: 'FeatureCollection',
      features
    };
    this.cachedGeoJsonKey = cacheKey;
  }

  private showStrikePopup(coords: [number, number], ageMin: number, densKm2: number, hits: number): void {
    const ageText =
      ageMin === 0
        ? '0–5 min ago (Active Strike)'
        : ageMin < 60
        ? `${ageMin} min ago`
        : `${Math.floor(ageMin / 60)}h ${ageMin % 60}m ago`;

    const isMetric = store.getState().units === 'metric';
    const densityText = isMetric
      ? `${densKm2.toFixed(3)} fl/km&sup2;/min`
      : `${(densKm2 * 2.58999).toFixed(2)} fl/mi&sup2;/min (${densKm2.toFixed(2)} /km&sup2;)`;

    const html = `
      <div style="padding: 3px 4px; font-family: system-ui, sans-serif; font-size: 0.76rem; line-height: 1.45;">
        <div style="font-weight: 800; color: #ffe800; margin-bottom: 2px;">⚡ NOAA MRMS CG Lightning</div>
        <div><strong style="color: #cbd5e1;">Recency:</strong> <span style="color: #f8fafc;">${ageText}</span></div>
        <div><strong style="color: #cbd5e1;">Peak Density:</strong> <span style="color: #f8fafc;">${densityText}</span></div>
        <div><strong style="color: #cbd5e1;">5m Cell Hits (8h):</strong> <span style="color: #f8fafc;">${hits || 1}</span></div>
      </div>
    `;

    if (!this.hoverPopup) {
      this.hoverPopup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 10
      });
    }
    this.hoverPopup.setLngLat(coords).setHTML(html).addTo(this.map);
  }

  private handleCanvasHover(ev: any): void {
    if (!this.active || !this.useCanvasComposite || this.cachedItems.length === 0) {
      if (this.canvasHoverActive) {
        this.canvasHoverActive = false;
        this.map.getCanvas().style.cursor = '';
        if (this.hoverPopup) this.hoverPopup.remove();
      }
      return;
    }
    // Skip hover hit-testing while dragging/panning
    if (ev?.originalEvent && ev.originalEvent.buttons !== 0) {
      return;
    }
    const nowMs = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (nowMs - this.lastHoverCheckMs < 32) {
      return;
    }
    this.lastHoverCheckMs = nowMs;

    const ptCursor = ev?.point;
    const lngLat = ev?.lngLat;
    if (!ptCursor || !lngLat) return;

    const zoom = this.map.getZoom();
    const zoomFactor = this.evaluateZoomScale(zoom);
    const maxHitPx = Math.max(10, 18 * zoomFactor * this.cfg.densityScaleMax);
    const degTol = Math.max(0.05, (maxHitPx / (512 * Math.pow(2, zoom))) * 360 * 1.8);

    const curLat = lngLat.lat;
    const curLon = lngLat.lng;
    let bestHit: TrailFeatureItem | null = null;

    // Iterate newest/topmost -> oldest so overlapping cluster hover picks the topmost active bolt
    for (let i = this.cachedItems.length - 1; i >= 0; i--) {
      const it = this.cachedItems[i];
      if (Math.abs(it.lat - curLat) > degTol || Math.abs(it.lon - curLon) > degTol) {
        continue;
      }
      const pt = this.map.project([it.lon, it.lat]);
      const hitRad = Math.max(8, 16 * zoomFactor * it.dScale);
      const dx = pt.x - ptCursor.x;
      const dy = pt.y - ptCursor.y;
      if (dx * dx + dy * dy <= hitRad * hitRad) {
        bestHit = it;
        break;
      }
    }

    if (bestHit) {
      this.canvasHoverActive = true;
      this.map.getCanvas().style.cursor = 'pointer';
      this.showStrikePopup([bestHit.lon, bestHit.lat], bestHit.ageMin, bestHit.density, bestHit.hits);
    } else if (this.canvasHoverActive) {
      this.canvasHoverActive = false;
      this.map.getCanvas().style.cursor = '';
      if (this.hoverPopup) {
        this.hoverPopup.remove();
      }
    }
  }

  private setupInteractions(): void {
    if (this.interactionsBound) return;
    this.interactionsBound = true;

    this.map.on('mousemove', this.layerId, (ev: any) => {
      if (
        !this.active ||
        this.useCanvasComposite ||
        (ev?.originalEvent && ev.originalEvent.buttons !== 0) ||
        !ev.features ||
        ev.features.length === 0
      ) {
        return;
      }
      this.map.getCanvas().style.cursor = 'pointer';
      const feat = ev.features[0];
      const props = feat.properties || {};
      const coords = (feat.geometry as any).coordinates.slice() as [number, number];
      this.showStrikePopup(
        coords,
        Number(props.ageMin) || 0,
        Number(props.density) || 0,
        Number(props.hits) || 1
      );
    });

    this.map.on('mouseleave', this.layerId, () => {
      if (this.useCanvasComposite) return;
      this.map.getCanvas().style.cursor = '';
      if (this.hoverPopup) {
        this.hoverPopup.remove();
      }
    });
  }

  private syncMapLibreLayer(): void {
    if (!this.map.getStyle()) return;

    // When compositing on the z-index 3 HiDPI Canvas above DOM Radar/Satellite overlays,
    // suspend the WebGL symbol layer completely (hover hit-testing is handled directly in JS)
    // to avoid duplicate rendering and MapLibre GeoJSON worker serialization overhead.
    if (this.useCanvasComposite) {
      if (this.map.getLayer(this.layerId) && this.lastSyncedVisible !== false) {
        this.map.setLayoutProperty(this.layerId, 'visibility', 'none');
        this.lastSyncedVisible = false;
      }
      return;
    }

    if (!this.map.hasImage(`${this.spritePrefix}0`)) {
      this.registerMapLibreSprites();
    }

    const existingSource = this.map.getSource(this.sourceId) as maplibregl.GeoJSONSource | undefined;
    if (!existingSource) {
      this.map.addSource(this.sourceId, {
        type: 'geojson',
        data: this.cachedGeoJson,
        buffer: 64
      });
      this.lastSyncedGeoJsonKey = this.cachedGeoJsonKey;
    } else if (this.lastSyncedGeoJsonKey !== this.cachedGeoJsonKey) {
      existingSource.setData(this.cachedGeoJson);
      this.lastSyncedGeoJsonKey = this.cachedGeoJsonKey;
    }

    if (!this.map.getLayer(this.layerId)) {
      this.map.addLayer({
        id: this.layerId,
        type: 'symbol',
        source: this.sourceId,
        layout: {
          'visibility': this.active ? 'visible' : 'none',
          'icon-image': ['concat', this.spritePrefix, ['to-string', ['get', 'bucket']]] as any,
          'icon-size': this.buildIconSizeExpression() as any,
          'icon-allow-overlap': this.cfg.allowOverlap,
          'icon-ignore-placement': this.cfg.allowOverlap,
          'icon-padding': 1,
          'symbol-sort-key': ['get', 'sortKey'] as any
        },
        paint: {
          'icon-opacity': ['*', this.currentOpacity, ['get', 'ageAlpha']] as any
        }
      });
      this.lastSyncedVisible = this.active;
      this.lastSyncedOpacity = this.currentOpacity;
      this.setupInteractions();
    } else {
      if (this.lastSyncedVisible !== this.active) {
        this.map.setLayoutProperty(this.layerId, 'visibility', this.active ? 'visible' : 'none');
        this.lastSyncedVisible = this.active;
      }
      if (Math.abs(this.lastSyncedOpacity - this.currentOpacity) > 0.001) {
        this.map.setPaintProperty(
          this.layerId,
          'icon-opacity',
          ['*', this.currentOpacity, ['get', 'ageAlpha']] as any
        );
        this.lastSyncedOpacity = this.currentOpacity;
      }
    }
  }

  private clearOverlayCanvas(): void {
    if (!this.overlayHasContent) return;
    const container = this.map.getContainer();
    this.overlayCtx.clearRect(0, 0, container.clientWidth, container.clientHeight);
    this.overlayHasContent = false;
    this.lastDrawnCanvasKey = '';
  }

  private redrawOverlayCanvas(skipIfUnchanged: boolean = false): void {
    if (!this.active || !this.useCanvasComposite || this.cachedItems.length === 0) {
      this.clearOverlayCanvas();
      return;
    }

    const canvasStateKey = `${this.cachedGeoJsonKey}|${this.currentOpacity.toFixed(3)}`;
    if (skipIfUnchanged && this.overlayHasContent && this.lastDrawnCanvasKey === canvasStateKey) {
      return;
    }

    const container = this.map.getContainer();
    const viewW = container.clientWidth;
    const viewH = container.clientHeight;
    this.overlayCtx.clearRect(0, 0, viewW, viewH);

    const zoom = this.map.getZoom();
    const zoomFactor = this.evaluateZoomScale(zoom);
    const baseLogicalPx = 32 * zoomFactor;
    const margin = 48;

    // Geographic frustum pre-culling avoids projecting off-screen bolts across the continent
    const bounds = this.map.getBounds();
    let west = -180;
    let east = 180;
    let south = -85;
    let north = 85;
    if (bounds) {
      const degPad = Math.max(0.5, 360 / Math.pow(2, zoom + 1.5));
      west = bounds.getWest() - degPad;
      east = bounds.getEast() + degPad;
      south = bounds.getSouth() - degPad;
      north = bounds.getNorth() + degPad;
    }

    // Fast affine Web Mercator screen projection when unpitched & north-up (zero per-bolt LngLat/Point allocations)
    const canFastProject =
      Math.abs(this.map.getPitch()) < 0.01 &&
      Math.abs(this.map.getBearing()) < 0.01;

    let p0x = 0;
    let p0y = 0;
    let m0X = 0;
    let m0Y = 0;
    let scaleX = 1;
    let scaleY = 1;
    if (canFastProject) {
      const center = this.map.getCenter();
      const lon0 = center.lng - 10;
      const lat0 = Math.min(80, Math.max(-75, center.lat + 8));
      const lon1 = center.lng + 10;
      const lat1 = Math.max(-80, Math.min(75, center.lat - 8));
      const p0 = this.map.project([lon0, lat0]);
      const p1 = this.map.project([lon1, lat1]);
      m0X = lonToMercX(lon0);
      m0Y = latToMercY(lat0);
      const m1X = lonToMercX(lon1);
      const m1Y = latToMercY(lat1);
      p0x = p0.x;
      p0y = p0.y;
      scaleX = (p1.x - p0.x) / (m1X - m0X);
      scaleY = (p1.y - p0.y) / (m1Y - m0Y);
    }

    this.overlayCtx.imageSmoothingEnabled = true;

    let lastAlpha = -1;
    let drewAny = false;
    for (let i = 0; i < this.cachedItems.length; i++) {
      const it = this.cachedItems[i];
      if (it.lat < south || it.lat > north || it.lon < west || it.lon > east) {
        continue;
      }

      let px: number;
      let py: number;
      if (canFastProject) {
        px = p0x + (it.mercX - m0X) * scaleX;
        py = p0y + (it.mercY - m0Y) * scaleY;
      } else {
        const pt = this.map.project([it.lon, it.lat]);
        px = pt.x;
        py = pt.y;
      }

      if (px < -margin || px > viewW + margin || py < -margin || py > viewH + margin) {
        continue;
      }
      const sprite = this.spriteCanvases[it.bucket] || this.spriteCanvases[0];
      if (!sprite) continue;
      const drawSize = baseLogicalPx * it.dScale;
      const half = drawSize * 0.5;
      const alpha = Math.max(0, Math.min(1, this.currentOpacity * it.ageAlpha));
      if (Math.abs(alpha - lastAlpha) > 0.004) {
        this.overlayCtx.globalAlpha = alpha;
        lastAlpha = alpha;
      }
      this.overlayCtx.drawImage(sprite, px - half, py - half, drawSize, drawSize);
      drewAny = true;
    }
    this.overlayCtx.globalAlpha = 1.0;
    this.overlayHasContent = drewAny;
    this.lastDrawnCanvasKey = canvasStateKey;
  }

  public update(
    historyUrl: string | null,
    targetEpochSec: number,
    opacity: number,
    active: boolean,
    compositeAboveDomCanvases: boolean = false
  ): void {
    this.currentHistoryUrl = historyUrl;
    this.targetEpochSec = targetEpochSec;
    this.currentOpacity = opacity;
    this.active = active;
    this.useCanvasComposite = compositeAboveDomCanvases;

    if (!active || !historyUrl) {
      this.clear();
      return;
    }

    const applyHistory = (history: LightningHistoryPayload) => {
      if (!this.active || this.currentHistoryUrl !== historyUrl) return;
      this.latestHistory = history;
      this.buildRollingTrail(history, this.targetEpochSec);
      this.syncMapLibreLayer();
      if (this.useCanvasComposite) {
        this.redrawOverlayCanvas(true);
      } else {
        this.clearOverlayCanvas();
      }
    };

    const cached = this.historyCache.get(historyUrl);
    if (cached) {
      applyHistory(cached);
    } else {
      // Render immediately with prior cached history while the new 5m snapshot loads
      if (this.latestHistory) {
        this.buildRollingTrail(this.latestHistory, this.targetEpochSec);
        this.syncMapLibreLayer();
        if (this.useCanvasComposite) {
          this.redrawOverlayCanvas(true);
        }
      }
      void this.fetchHistory(historyUrl).then((loaded) => {
        if (loaded) applyHistory(loaded);
      });
    }
  }

  public redraw(): void {
    if (this.active && this.useCanvasComposite) {
      this.lastDrawnCanvasKey = '';
      this.redrawOverlayCanvas(false);
    }
  }

  public clearCache(): void {
    this.historyCache.clear();
    this.pendingFetches.clear();
    this.latestHistory = null;
    this.cachedGeoJsonKey = '';
    this.lastSyncedGeoJsonKey = '';
    this.lastDrawnCanvasKey = '';
  }

  public clear(): void {
    this.active = false;
    this.clearOverlayCanvas();
    if (this.canvasHoverActive) {
      this.canvasHoverActive = false;
      this.map.getCanvas().style.cursor = '';
    }
    if (this.hoverPopup) {
      this.hoverPopup.remove();
    }
    if (this.map.getStyle() && this.map.getLayer(this.layerId) && this.lastSyncedVisible !== false) {
      this.map.setLayoutProperty(this.layerId, 'visibility', 'none');
      this.lastSyncedVisible = false;
    }
  }

  public destroy(): void {
    this.clear();
    this.map.off('move', this.onMapMoveBound);
    this.map.off('resize', this.onMapResizeBound);
    this.map.off('styledata', this.onStyleDataBound);
    this.map.off('mousemove', this.onCanvasMouseMoveBound);
    if (this.map.getStyle()) {
      if (this.map.getLayer(this.layerId)) {
        this.map.removeLayer(this.layerId);
      }
      if (this.map.getSource(this.sourceId)) {
        this.map.removeSource(this.sourceId);
      }
    }
    if (this.overlayCanvas && this.overlayCanvas.parentNode) {
      this.overlayCanvas.parentNode.removeChild(this.overlayCanvas);
    }
  }
}

export default VectorLightningRenderer;
